import React, { useCallback, useEffect, useContext } from 'react';
import SuperTable from '@nile/components/src/Components/SuperTable/SuperTable';
import { Modal, Link, TextField } from '@superops/ui-design-system';
import {
  CurrencyText,
  getTranslatedText,
  SkeletonTableLoading,
  Loader
} from '@nile/components';
import { useIntl } from 'react-intl';
import BigNumber from 'bignumber.js';
import { formatDecimalWithBigNumber } from '@nile/client_management/src/Utils/invoiceAndQuoteHelper';
import CurrencyCell from '@nile/client_management/src/Pages/Invoice/CurrencyCell';
import InvoiceStatusCell from '@nile/client_management/src/Pages/Protected/ClientNavigation/InvoiceStatusCell';
import TicketContext from '@nile/mangifera/src/Contexts/TicketContext';
import DeleteCell from './DeleteCell';

export function InvoiceRedirectCell({ cell }) {
  const ticketContext = useContext(TicketContext);

  return (
    <Link
      removeUnderline
      onClick={() => {
        ticketContext.openTab({
          module: 'INVOICE',
          data: {
            invoiceId: cell.row.original.invoice.invoiceId
          }
        });
      }}
    >
      {cell.value}
    </Link>
  );
}

function TextFieldCell(props) {
  const { cell } = props;
  const transaction = cell.row.original;
  const { actions, allData, isEditable } = cell.column;

  const isDisabled = false;

  if (isEditable) {
    return (
      <TextField
        disabled={isDisabled}
        autoComplete="off"
        type="number"
        min={0}
        onChange={(changedVal) => {
          actions.onTextChange({
            value: changedVal ? parseFloat(changedVal) : 0,
            original: transaction
          });
        }}
        onBlur={() => {
          // Format the amount to 2 decimal places with ROUND_HALF_UP on blur
          const formattedAmount = formatDecimalWithBigNumber(
            transaction.amount,
            2
          );

          actions.onTextChange({
            value: formattedAmount,
            original: transaction
          });
        }}
        value={String(transaction.amount) ?? '0'}
        error={transaction.error && 'error'}
        prefix={<CurrencyText symbolOnly />}
        hideErrorMessage
      />
    );
  }

  return <CurrencyCell {...props} />;
}

const defaultColumnCells = {
  InvoiceRedirectCell,
  CurrencyCell,
  DeleteCell,
  TextFieldCell,
  InvoiceStatusCell
};

export function InvoiceTransactionListTable({
  transactions,
  hideDelete,
  onDelete,
  isEditable,
  receivedAmount,
  onTransactionChange,
  originalTransactions,
  isLoading
}) {
  const intl = useIntl();
  const receivedAmountRef = React.useRef(receivedAmount);
  receivedAmountRef.current = receivedAmount;
  const [listLoading, setListLoading] = React.useState(false);
  const [columnGroup, setColumnGroup] = React.useState([
    { Header: 'Name', header: 'name', columns: [] }
  ]);
  const [listInfo, setListInfo] = React.useState({
    totalCount: 0,
    pageSize: 10,
    page: 1
  });

  /**
   * Calculate max limit for a transaction when editing payments with multiple transactions for same invoice
   *
   * Scenario: Invoice 1 total is $16
   * - Original Payment 1: $8
   * - Payment 2: $2
   * - Total paid: $10
   *
   * When editing Payment 1 to have two transactions for same invoice:
   * - Transaction A: $9 for Invoice 1
   * - Transaction B: $3 for Invoice 1
   * - New total paid: $14 ($9 + $3 + $2 from Payment 2)
   *
   * For Transaction A max limit calculation:
   * - invoice.amountPaid = $14 (current total paid across all payments)
   * - originalCurrentPaymentTotalForInvoice = $12 ($9 + $3 original total)
   * - currentPaymentOtherTransactionsForInvoice = $3 (Transaction B amount)
   * - amountPaidFromOtherPayments = $14 - $12 = $2 (Payment 2)
   * - maxLimit = $16 - $2 - $3 = $11
   */
  const computeMaxLimit = useCallback(
    (transaction, allCurrentTransactions = []) => {
      const { invoice } = transaction;

      // Calculate the original total amount for this invoice from the current payment
      const originalCurrentPaymentTotalForInvoice =
        originalTransactions
          ?.filter((o) => o.invoice.invoiceId === invoice.invoiceId)
          ?.reduce(
            (sum, tx) => sum.plus(new BigNumber(tx.amount || 0)),
            new BigNumber(0)
          )
          ?.toFixed(2) || '0.00';

      // Calculate current total amount for this invoice from other transactions in current payment (excluding the current transaction)
      const currentPaymentOtherTransactionsForInvoice =
        allCurrentTransactions
          ?.filter(
            (t) =>
              t.invoice.invoiceId === invoice.invoiceId &&
              t.transactionId !== transaction.transactionId
          )
          ?.reduce(
            (sum, tx) => sum.plus(new BigNumber(tx.amount || 0)),
            new BigNumber(0)
          )
          ?.toFixed(2) || '0.00';

      // Amount paid from other payments (not the current payment being edited)
      const amountPaidFromOtherPayments = new BigNumber(invoice.amountPaid || 0)
        .minus(new BigNumber(originalCurrentPaymentTotalForInvoice))
        .toFixed(2);

      // Maximum amount available for this transaction
      const maxLimit = new BigNumber(invoice.totalAmount || 0)
        .minus(new BigNumber(amountPaidFromOtherPayments))
        .minus(new BigNumber(currentPaymentOtherTransactionsForInvoice))
        .toFixed(2);

      return Math.max(0, Number(maxLimit));
    },
    [originalTransactions]
  );

  function setTransactions(t) {
    onTransactionChange?.(t);
  }

  const transactionsRef = React.useRef(transactions);
  transactionsRef.current = transactions;

  useEffect(() => {
    if (!transactionsRef.current?.length || !isEditable) return;

    const totalAmount = transactionsRef.current
      .reduce(
        (acc, item) => acc.plus(new BigNumber(item.amount || 0)),
        new BigNumber(0)
      )
      .toFixed(2);

    setTransactions(
      transactionsRef.current.map((t) => {
        const maxLimit = computeMaxLimit(t, transactionsRef.current);
        return {
          ...t,
          error:
            /* Amount in the TextField will be converted to two decimals after blur,
             *  value remains unchanged in onChange in order to prevent the error from happening in the field
             *  we need to format the amount to two decimals */
            formatDecimalWithBigNumber(t.amount, 2) > maxLimit ||
            t.amount === 0 ||
            Number(totalAmount) > Number(receivedAmountRef.current)
        };
      })
    );
  }, [receivedAmount, computeMaxLimit, isEditable, transactions?.length]);

  function fetchViewData() {
    // setListLoading(true);
    // const resp = {};
    // setListLoading(false);
    const cols = [
      {
        name: 'invoice.displayId',
        label: getTranslatedText(intl, 'invoice.details.invoiceId'),
        type: 'InvoiceRedirectCell'
      },
      {
        name: 'invoice.totalAmount',
        label: getTranslatedText(intl, 'invoice.partialPayment.invoiceTotal'),
        type: 'CurrencyCell'
      },
      {
        name: 'invoice.statusEnum',
        label: getTranslatedText(intl, 'client.navigation.invoice.list.status'),
        type: 'InvoiceStatusCell'
      },
      {
        name: 'amount',
        label: getTranslatedText(intl, 'invoice.partialPayments.appliedAmount'),
        type: 'TextFieldCell'
      }
    ]
      .filter(Boolean)
      .map((col) => {
        return {
          Header: col.label,
          accessor: col.name,
          accessorKey: col.name,
          // minWidth: col.name === 'subject' ? 400 : 50,
          // width: col.name === 'invoiceDescription' ? 400 : 150,
          type: col.type || 'Text',
          id: col.name,
          disableResizing: false,
          className: 'sticky',
          disableSortBy: true,
          editable: false,
          disableFilters: true,
          isBold: col.name === 'amount',
          isEditable: isEditable,
          allData: transactionsRef.current,
          actions: {
            onTextChange: ({ value, original }) => {
              const newTransactionList = transactionsRef.current.map((item) => {
                if (item.transactionId === original.transactionId) {
                  return {
                    ...item,
                    amount: value
                  };
                }

                return item;
              });

              const totalAmount = newTransactionList
                .reduce(
                  (acc, item) => acc.plus(new BigNumber(item.amount || 0)),
                  new BigNumber(0)
                )
                .toFixed(2);

              return setTransactions(
                newTransactionList.map((t) => {
                  const maxLimit = computeMaxLimit(t, newTransactionList);

                  return {
                    ...t,
                    error:
                      /* Amount in the TextField will be converted to two decimals after blur,
                       *  value remains unchanged in onChange in order to prevent the error from happening in the field
                       *  we need to format the amount to two decimals */
                      formatDecimalWithBigNumber(t.amount, 2) > maxLimit ||
                      t.amount === 0 ||
                      Number(totalAmount) > Number(receivedAmountRef.current)
                  };
                })
              );
            }
          }
        };
      });

    if (!hideDelete) {
      cols.push({
        Header: ' ',
        accessor: ' ',
        accessorKey: ' ',
        type: 'DeleteCell',
        id: 'delete',
        disableResizing: true,
        className: 'sticky',
        disableSortBy: true,
        disableFilters: true,
        actions: {
          onChange: ({ miscItem: row }) => {
            onDelete?.(row);
          }
        }
      });
    }

    setColumnGroup([{ Header: 'Name', header: 'name', columns: cols }]);
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div
      className={`${!transactions?.length ? 'height300' : 'max-height-300'}`}
    >
      <SuperTable
        context={{}}
        columns={columnGroup}
        data={transactions}
        //   rowCount={20}
        refreshTable={fetchViewData}
        listInfo={listInfo}
        pagination={Math.ceil(listInfo?.totalCount / listInfo?.pageSize) > 1}
        pages={Math.ceil(listInfo?.totalCount / listInfo?.pageSize) || 0}
        padded="lg"
        recordCount={listInfo?.totalCount || 0}
        defaultColumnCells={defaultColumnCells}
        activePage={listInfo?.page || 0}
        isLoading={listLoading}
        noBulkSelect
        fullHeight
        idKey="transactionId"
      />
    </div>
  );
}
